import I18n from 'I18n';
import { isEmpty } from 'underscore';
import { FALLBACK_DATE_FORMAT, FALLBACK_DATE_SEPARATOR } from 'forms-utils-lib/date/DateConstants';
import { getFormattedDate } from 'forms-utils-lib/date/getFormattedDate';
import { fromString, toFormattedString, fromMoment, toMoment } from 'foundations-components/transitional/utils/SimpleDate';
import { DATE_FIELD_FORMAT } from 'FormsUI/constants/DateFormats';
import getMetaDataValue from 'forms-utils-lib/formField/utils/getMetaDataValue';
import updateFormFieldKey from 'forms-utils-lib/formField/utils/updateFormFieldKey';
import FormFieldKeys from 'forms-utils-lib/constants/formField/FormFieldKeys';
const LOCALISED_TIME_FORMAT = 'LT z';
const LOCALISED_ONLY_DATE_FORMAT = 'LL';
const LOCALISED_DATE_FORMAT = 'LLL z';
export function getDaysFromToday(timestamp) {
  return I18n.moment().diff(timestamp, 'days');
}
export function getLocalisedDateFromTimestamp(timestamp, timezone = I18n.moment.portalTz) {
  return timezone(timestamp).format(LOCALISED_DATE_FORMAT);
}
export function formatDateForDateInput(date) {
  if (isEmpty(date)) {
    return undefined;
  }
  const formattedDate = fromString(date, DATE_FIELD_FORMAT);
  return formattedDate || date;
}
export function getLocalisedTimeFromTimestamp(timestamp, timezone = I18n.moment.portalTz) {
  return timezone(timestamp).format(LOCALISED_TIME_FORMAT);
}
export function getLocalisedOnlyDateFromTimestamp(timestamp, timezone = I18n.moment.portalTz) {
  return timezone(timestamp).format(LOCALISED_ONLY_DATE_FORMAT);
}
export const getLocalizedOnlyUtcDateFromTimestamp = timestamp => I18n.moment.utc(timestamp).format(LOCALISED_ONLY_DATE_FORMAT);
export const getFormatValueFromMetaData = field => ({
  format: getMetaDataValue(field, 'format') || FALLBACK_DATE_FORMAT,
  separator: getMetaDataValue(field, 'separator') || FALLBACK_DATE_SEPARATOR
});
export const getFormatTextFromMetaData = field => field.metaData ? getFormattedDate(getMetaDataValue(field, 'format') || FALLBACK_DATE_FORMAT, getMetaDataValue(field, 'separator') || FALLBACK_DATE_SEPARATOR) : FALLBACK_DATE_FORMAT;
export const updateDateFormatMetaData = (field, update, evt) => update(updateFormFieldKey(field, FormFieldKeys.META_DATA, [{
  name: 'format',
  value: evt.target.value.format
}, {
  name: 'separator',
  value: evt.target.value.separator
}, ...field.metaData.filter(metaDataItem => metaDataItem.name !== 'format' && metaDataItem.name !== 'separator')]));
export const updateDefaultDate = (field, update, value) => {
  const newValue = value ? toFormattedString(value, DATE_FIELD_FORMAT) : '';
  update(updateFormFieldKey(field, FormFieldKeys.DEFAULT_VALUE, newValue));
};
export const getDateFieldValidationMessage = date => {
  if (isEmpty(date)) {
    return null;
  }
  return isEmpty(fromString(date, DATE_FIELD_FORMAT)) ? 'editor.form.config.selectDateFormat.validation' : null;
};
const isUTCOffset = zoneId => {
  return zoneId === 'Z' || /^[-+]\d\d:\d\d$/.test(zoneId);
};
export const convertTimePointToMomentDate = timePoint => {
  if (isUTCOffset(timePoint.zoneId)) {
    return I18n.moment.utc({
      year: timePoint.year,
      month: timePoint.month - 1,
      date: timePoint.day
    }).utcOffset(timePoint.zoneId, true);
  }
  return I18n.moment.tz({
    year: timePoint.year,
    month: timePoint.month - 1,
    date: timePoint.day
  }, timePoint.zoneId);
};
export const convertMomentDateToTimePoint = (date, dayAlignment) => {
  let time = {};
  if (dayAlignment === 'start') {
    date.startOf('date');
    time = {
      hour: date.hour(),
      minute: date.minute(),
      second: date.second(),
      millisecond: date.millisecond()
    };
  } else if (dayAlignment === 'end') {
    date.endOf('date');
    time = {
      hour: date.hour(),
      minute: date.minute(),
      second: date.second(),
      millisecond: date.millisecond()
    };
  }
  return Object.assign({
    timeType: 'DATE',
    year: date.year(),
    month: date.month() + 1,
    day: date.date()
  }, time, {
    zoneId: date.tz() || 'UTC'
  });
};
export const convertTimePointToSimpleDate = timePoint => {
  const momentTime = convertTimePointToMomentDate(timePoint);
  return fromMoment(momentTime);
};
export const converSimpleDateToTimePointStart = date => convertMomentDateToTimePoint(toMoment(date), 'start');
export const converSimpleDateToTimePointEnd = date => convertMomentDateToTimePoint(toMoment(date), 'end');
export const getStartAndNextDayTimePoints = () => {
  const momentTimezone = I18n.moment.portalTz;
  const startOfDay = momentTimezone().startOf('day');
  const startOfNextDay = momentTimezone().startOf('day').add(1, 'days');
  const startOfDayTimePoint = convertMomentDateToTimePoint(startOfDay, 'start');
  const endOfNextDayTimePoint = convertMomentDateToTimePoint(startOfNextDay, 'end');
  // provides a check in case date.tz() returns undefined
  const isUTC = startOfDayTimePoint.zoneId === 'UTC' || endOfNextDayTimePoint.zoneId === 'UTC';
  return {
    startOfDayTimePoint,
    endOfNextDayTimePoint,
    isUTC
  };
};